import { Space } from '@signal-os/lock-space-meili-docs-types';
import meiliSearchApiClient from 'src/api/clients/meilisearch';
import tenantApi from 'src/api/clients/tenant';
import { SearchState } from 'src/hooks/meilisearch/types/search-state';
import { MeiliSearchResponse } from 'src/types/responses/meilisearch';
import { calculatePaginationOffset } from 'src/utils/calculate-pagination-offset';
import { PaginatedData } from '../../../../types/interfaces/paginated-data';
import { BlockSpacePayload } from '../types/payload/block-space.payload';
import { CreateSpacePayload } from '../types/payload/create-space.payload';
import { DeleteSpacePayload } from '../types/payload/delete-space.payload';
import { EditSpacePayload } from '../types/payload/edit-space.payload';
import { CreateSpaceMapPayload } from '../types/payload/save-space-map.payload';
import { UnblockSpacePayload } from '../types/payload/unblock-space.payload';
import { SpaceMapResponse } from '../types/response/space-map.response';
import { UploadSpaceMapImageResponse } from '../types/response/upload-space-map-image.response';

export const fetchSpaces = async (
  tenantToken: string,
  searchState: SearchState,
  page: number,
  perPage: number,
) => {
  const { data } = await meiliSearchApiClient.post<MeiliSearchResponse<Space>>(
    `/indexes/Space-${tenantToken}/search`,
    {
      ...searchState,
      limit: perPage,
      offset: calculatePaginationOffset(page, perPage),
    },
  );

  return data;
};

export const createSpace = async (
  payload: CreateSpacePayload,
): Promise<string> => {
  const { data } = await tenantApi.post<{ token: string }>('/spaces', payload);

  return data.token;
};

export const uploadSpaces = async (file: File): Promise<void> => {
  const formData = new FormData();
  formData.append('file', file, file.name);

  await tenantApi.post<void>('/spaces/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const deleteSpace = async (
  payload: DeleteSpacePayload,
): Promise<void> => {
  await tenantApi.post<void>(`/spaces/${payload.spaceToken}/delete`, {
    bookingsDecisions: payload.bookingsDecisions,
    bookingsDecisionsReason: payload.bookingsDecisionsReason,
  });
};

export const updateSpace = async (
  spaceToken: string,
  payload: EditSpacePayload,
): Promise<void> => {
  await tenantApi.put<void>(`/spaces/${spaceToken}`, payload);
};

export const blockSpace = async (
  spaceToken: string,
  payload: BlockSpacePayload,
): Promise<void> => {
  await tenantApi.patch<void>(`/spaces/${spaceToken}/block`, payload);
};

export const unblockSpace = async (
  spaceToken: string,
  payload: UnblockSpacePayload,
): Promise<void> => {
  await tenantApi.patch<void>(`/spaces/${spaceToken}/unblock`, payload);
};

export const fetchSpaceMaps = async (
  floor: string,
  page: number,
  perPage: number,
) => {
  const { data } = await tenantApi.get<PaginatedData<SpaceMapResponse>>(
    `/space-maps/${floor}`,
    {
      params: {
        limit: perPage,
        offset: calculatePaginationOffset(page, perPage),
      },
    },
  );

  return data;
};

export const saveSpaceMapAsDraft = async (payload: CreateSpaceMapPayload) => {
  const { data } = await tenantApi.post<{ token: string }>(
    '/space-maps',
    payload,
  );

  return data.token;
};

export const publishSpaceMap = async (spaceMapToken: string) => {
  await tenantApi.post<void>(`/space-maps/${spaceMapToken}/publish`);
};

export const uploadSpaceMapImage = async (file: File) => {
  const formData = new FormData();

  formData.append('image', file, file.name);

  const { data } = await tenantApi.post<UploadSpaceMapImageResponse>(
    `/space-maps/upload-background`,
    formData,
  );

  return data;
};

export interface GenerateFloorMapTilesPayload {
  file: File;
  spaceMapToken: string;
  minZoom: number;
  maxZoom: number;
  tileSize: number;
  unitsPerPixel: number;
  format: string;
  resampling: string;
}

export const generateFloorMapTiles = async (
  payload: GenerateFloorMapTilesPayload,
): Promise<GenerateFloorMapTilesResponse> => {
  const formData = new FormData();

  formData.append('file', payload.file);
  formData.append('spaceMapToken', payload.spaceMapToken);
  formData.append('minZoom', payload.minZoom.toString());
  formData.append('maxZoom', payload.maxZoom.toString());
  formData.append('tileSize', payload.tileSize.toString());
  formData.append('unitsPerPixel', payload.unitsPerPixel.toString());
  formData.append('format', payload.format);
  formData.append('resampling', payload.resampling);

  // TODO: should be /space-maps/tiles on the API side, change later after backend adjusts
  const { data } = await tenantApi.post<GenerateFloorMapTilesResponse>(
    '/space-map/tiles',
    formData,
  );

  return data;
};

export interface GenerateFloorMapTilesResponse {
  success: boolean;
  spaceMapToken: string;
  width: number;
  height: number;
  minZoom: number;
  maxZoom: number;
  metadata: Metadata;
  storage: string;
  originalFileName: string;
  originalContentType: string;
  gcpBucket: string;
  gcpPath: string;
  tileBaseUrl: string;
  uploadedFiles: number;
  metadataUrl: string;
  metadataGcpPath: string;
  originalUrl: string;
  originalGcpPath: string;
  previewUrl: string;
  previewGcpPath: string;
  mode: string;
  durationMs: number;
  peakMemoryMB: number;
}

export interface Metadata {
  name: string;
  version: string;
  description: string;
  attribution: string;
  type: string;
  format: string;
  minzoom: string;
  maxzoom: string;
  scale: string;
  profile: string;
  crs: string;
  extent: number[];
  tile_matrix: TileMatrix[];
}

export interface TileMatrix {
  id: string;
  tile_size: number[];
  origin: number[];
  extent: number[];
  pixel_size: number[];
  matrix_size: number[];
  scale_denominator: number;
}
